<div class="flex flex-col items-center w-full h-full overflow-hidden pt-12 pb-6 px-6 gap-6">

  <!-- Main content (top): begrenzte Höhe (flex-1/min-h-0), damit der DSGVO-Hinweis unten fixiert
       bleibt; das Scrollen bei aufgeklappten Listen passiert innerhalb der jeweiligen Kachel
       (siehe unten), nicht auf Seitenebene. -->
  <div class="flex flex-col gap-6 w-full max-w-[1200px] flex-1 min-h-0">

    <!-- Progress Card -->
    <div class="flex flex-row items-center gap-6 p-4 border border-gray-200 rounded-lg w-full">
      <div class="flex flex-col gap-1 flex-1 min-w-0">

        <!-- Title + Percentage -->
        <div class="flex flex-row justify-between items-center w-full">
          <div class="flex items-center gap-4">
            <fa-icon [icon]="faSpinnerThird" class="animate-spin text-green text-svg-16 shrink-0"></fa-icon>
            <span class="text-xl font-medium text-gray-900">Prüfung wird durchgeführt...</span>
          </div>
          <span class="text-xl font-medium text-green ml-4">{{ progressPercentage }}%</span>
        </div>

        <!-- Progress bar -->
        <div class="relative w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
          <div [style.width.%]="progressPercentage"
               class="absolute inset-y-0 left-0 bg-green rounded-full transition-all duration-300"></div>
        </div>

        <!-- Status row -->
        <div class="flex flex-row items-center gap-6 mt-1">
          <span class="text-sm text-gray-500 flex-1 truncate">{{ statusMessage || progress.checkName }}</span>
          @if (unitProgress; as units) {
            <!-- Nur die Zahlen hervorgehoben, die Wörter normal – gleiche Gewichtung wie bei
                 "Dauer" und "Gestartet" rechts daneben. -->
            <div class="flex flex-row items-center gap-1 shrink-0">
              <span class="text-sm font-medium text-gray-900">{{ units.done }}</span>
              <span class="text-sm text-gray-900">von</span>
              <span class="text-sm font-medium text-gray-900">{{ units.total }}</span>
              <span class="text-sm text-gray-900">Einzelprüfungen</span>
              @if (unitFailedCount > 0) {
                <span class="text-sm text-gray-400">·</span>
                <span class="text-sm font-medium text-red-600">{{ unitFailedCount }}</span>
                <span class="text-sm text-gray-900">fehlgeschlagen</span>
              }
            </div>
          }
          @if (elementProgress; as elements) {
            <!-- Zweite, feinere Skala: Elemente innerhalb der laufenden Prüfregel (Räume,
                 LV-Positionen, Kapitel). Ohne sie steht der Balken während einer Regel über ein
                 Raumbuch minutenlang still. Gleiche Gewichtung wie die Einheiten-Zeile. -->
            <div class="flex flex-row items-center gap-1 shrink-0">
              <span class="text-sm font-medium text-gray-900">{{ elements.done }}</span>
              <span class="text-sm text-gray-900">von</span>
              <span class="text-sm font-medium text-gray-900">{{ elements.total }}</span>
              <span class="text-sm text-gray-900">{{ elements.label }}</span>
            </div>
          }
          @if (progress.elapsedSeconds > 0) {
            <div class="flex flex-row items-center gap-1 shrink-0">
              <span class="text-sm text-gray-900">Dauer</span>
              <span class="text-sm font-medium text-gray-900">{{ elapsedFormatted }}</span>
            </div>
          }
          @if (progress.startedAt) {
            <div class="flex flex-row items-center gap-1 shrink-0">
              <span class="text-sm text-gray-900">Gestartet</span>
              <span class="text-sm font-medium text-gray-900">{{ progress.startedAt }} Uhr</span>
            </div>
          }
        </div>

      </div>
    </div>

    <!-- Summary Cards Row -->
    <div class="flex flex-row gap-6 w-full flex-1 min-h-0">

      <!-- Prüfregeln Card -->
      <div class="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg flex-1 min-w-0 min-h-0">

        <!-- Header -->
        <div class="flex flex-row items-center gap-2 w-full shrink-0">
          <fa-icon [icon]="faCircleCheck" class="text-base text-gray-900 shrink-0"></fa-icon>
          <span class="text-base font-normal text-gray-900 flex-1">Ausgewählte Prüfregeln</span>
          <button (click)="toggleRules()"
                  class="flex items-center justify-center p-2.5 text-gray-600 hover:text-gray-900 transition-colors shrink-0">
            <fa-icon [icon]="allRulesetsCollapsed ? faChevronDown : faChevronUp" class="text-sm"></fa-icon>
          </button>
        </div>

        <!-- Stats -->
        <div class="flex flex-row items-stretch gap-4 w-full shrink-0">
          <div class="flex flex-col items-center justify-center gap-0.5 flex-1 p-4 bg-gray-50 rounded-lg">
            <span class="text-base font-medium text-gray-900">{{ rulesetCount }}</span>
            <span class="text-xs text-gray-500">myChecks</span>
          </div>
          <div class="flex flex-col items-center justify-center gap-0.5 flex-1 p-4 bg-gray-50 rounded-lg">
            <span class="text-base font-medium text-gray-900">{{ ruleCount }}</span>
            <span class="text-xs text-gray-500">Prüfregeln</span>
          </div>
        </div>

        <!-- Regelset-Liste: Kopfzeilen bleiben immer sichtbar; das Kopf-Chevron klappt alle Gruppen
             gemeinsam auf/zu, ein Klick auf eine Gruppe klappt nur diese – beide auf demselben Set.
             Eigener Scrollbereich, damit eine aufgeklappte Gruppe die Kachel/Seite nicht streckt. -->
        <div class="flex-1 min-h-0 overflow-y-auto">
          @if (selectedRules.length === 0) {
            <p class="text-sm text-gray-400 text-center py-2">Keine Prüfregeln ausgewählt</p>
          } @else {
            <div class="flex flex-col gap-3 w-full">
              @for (ruleset of selectedRules; track ruleset.name) {
                <div (click)="toggleRulesetCollapse(ruleset.name)"
                     class="flex flex-col border border-gray-200 rounded-lg w-full cursor-pointer hover:bg-gray-50 transition-colors">
                  <!-- Ruleset header -->
                  <div class="flex flex-row items-center gap-2 px-4 py-2">
                    <span class="text-sm font-medium text-gray-900 flex-1 truncate">{{ ruleset.name }}</span>
                    @if (rulesInSet(ruleset); as rulesInSetCount) {
                      <span class="text-xs text-gray-500 whitespace-nowrap shrink-0">
                        {{ rulesInSetCount <= 1 ? '1 Regel' : rulesInSetCount + ' Regeln' }}
                      </span>
                    }
                    @if (getRulesetStatusLabel(ruleset.runStatus); as statusLabel) {
                      <span
                        [class]="'text-xs font-medium px-2.5 py-0.5 rounded-md shrink-0 ' + getRulesetStatusClass(ruleset.runStatus)">
                        {{ statusLabel }}
                      </span>
                    }
                    @if (ruleset.type) {
                      <span
                        [class]="'text-xs font-medium px-2.5 py-0.5 rounded-md shrink-0 ' + getBadgeClass(ruleset.type)">
                        {{ getRuleTypeLabel(ruleset.type) }}
                      </span>
                    }
                  </div>
                  <!-- Einzelregeln: sichtbar, solange diese Gruppe nicht eingeklappt ist -->
                  @if (!isRulesetCollapsed(ruleset.name)) {
                    @for (ruleName of (ruleset.ruleNames ?? []); track ruleName) {
                      <div class="flex flex-row items-center gap-3 px-4 py-1.5">
                        <fa-icon [icon]="faCircleCheck" class="text-sm text-gray-300 shrink-0"></fa-icon>
                        <span class="text-sm text-gray-900 truncate">{{ ruleName }}</span>
                      </div>
                    }
                  }
                </div>
              }
            </div>
          }
        </div>

      </div>

      <!-- Dateien Card -->
      <div class="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg flex-1 min-w-0 min-h-0">

        <!-- Header -->
        <div class="flex flex-row items-center gap-2 w-full shrink-0">
          <fa-icon [icon]="faFile" class="text-base text-gray-900 shrink-0"></fa-icon>
          <span class="text-base font-normal text-gray-900 flex-1">Ausgewählte Dateien</span>
          <button (click)="toggleFiles()"
                  class="flex items-center justify-center p-2.5 text-gray-600 hover:text-gray-900 transition-colors shrink-0">
            <fa-icon [icon]="allFileTypesCollapsed ? faChevronDown : faChevronUp" class="text-sm"></fa-icon>
          </button>
        </div>

        <!-- Stats -->
        <div class="flex flex-row items-stretch gap-4 w-full shrink-0">
          <div class="flex flex-col items-center justify-center gap-0.5 flex-1 p-4 bg-gray-50 rounded-lg">
            <span class="text-base font-medium text-gray-900">{{ fileCount }}</span>
            <span class="text-xs text-gray-500">Dateien</span>
          </div>
          <div class="flex flex-col items-center justify-center gap-0.5 flex-1 p-4 bg-gray-50 rounded-lg">
            <span class="text-base font-medium text-gray-900">{{ totalFileSizeFormatted }}</span>
            <span class="text-xs text-gray-500">Gesamtgröße</span>
          </div>
        </div>

        <!-- Datei-Liste: Gruppen-Kopfzeilen bleiben immer sichtbar; das Kopf-Chevron klappt alle
             Kategorien gemeinsam auf/zu, ein Klick auf eine Kategorie klappt nur diese – selbes Set.
             Eigener Scrollbereich, damit eine aufgeklappte Kategorie die Kachel/Seite nicht streckt. -->
        <div class="flex-1 min-h-0 overflow-y-auto">
          @if (filesByType.length === 0) {
            <p class="text-sm text-gray-400 text-center py-2">Keine Dateien ausgewählt</p>
          } @else {
            <div class="flex flex-col gap-3 w-full">
              @for (group of filesByType; track group.type) {
                <div (click)="toggleFileTypeCollapse(group.type)"
                     class="flex flex-col border border-gray-200 rounded-lg w-full cursor-pointer hover:bg-gray-50 transition-colors">
                  <!-- File type group header -->
                  <div class="flex flex-row items-center gap-2 px-4 py-2">
                    <span class="text-sm font-medium text-gray-900 flex-1 truncate">{{ group.label }}</span>
                    <span class="text-xs text-gray-500 whitespace-nowrap shrink-0">{{ group.files.length }} Dateien</span>
                    <span
                      class="text-xs text-gray-500 whitespace-nowrap shrink-0">{{ formatGroupSize(group.totalSize) }}</span>
                  </div>
                  <!-- Einzelne Dateien: sichtbar, solange diese Gruppe nicht eingeklappt ist -->
                  @if (!isFileTypeCollapsed(group.type)) {
                    @for (file of group.files; track file.name) {
                      <div class="flex flex-row items-center gap-3 px-4 py-1.5">
                        <fa-icon [icon]="getFileIcon(file.fileType)" class="text-sm text-gray-500 shrink-0"></fa-icon>
                        <span class="text-sm text-gray-900 flex-1 truncate">{{ file.name }}</span>
                        @if (file.size) {
                          <span
                            class="text-xs text-gray-500 whitespace-nowrap shrink-0">{{ formatGroupSize(file.size) }}</span>
                        }
                      </div>
                    }
                  }
                </div>
              }
            </div>
          }
        </div>

      </div>

    </div>
  </div>

  <!-- DSGVO Footer: außerhalb des scrollbaren Hauptinhalts, daher immer sichtbar. -->
  <div class="flex flex-row items-center gap-2 shrink-0">
    <fa-icon [icon]="faShieldCheck" class="text-sm text-gray-700"></fa-icon>
    <span class="text-sm font-medium text-gray-700">DSGVO-konforme &amp; verschlüsselte Datenverarbeitung</span>
  </div>

</div>

